import { useEffect, useState, type ReactElement } from 'react'
import { Smartphone } from 'lucide-react'
import { Button } from './ui/button.js'
import { TryOnPhoneDialog } from './TryOnPhoneDialog.js'
import { setLiveMode, withLiveParam } from '../state/useDemoMode.js'

/**
 * Local copy of the AppShell.tsx desktop hook. Kept inline (not extracted to
 * a shared util) because two callers don't justify a new file yet — when a
 * third caller appears, lift it to web/src/lib/.
 */
const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(min-width: 768px)').matches === true,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(min-width: 768px)')
    const update = (): void => setIsDesktop(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isDesktop
}

/**
 * Secondary CTA shown on each scan-instructions screen when the demo replay
 * path is active. Lets the user opt into the real Gemini Live + camera path
 * for the current scan and everything that follows.
 *
 * - Mobile: tap flips the URL to `?live=true` and re-renders in place via
 *   the `veritact:demo-mode-changed` event. The primary CTA then becomes
 *   "Open Camera".
 * - Desktop: tap opens a dialog with a QR code that encodes the current URL
 *   with `live=true` injected. A phone scanning the QR resumes the demo at
 *   the same step in live mode.
 */
export const TryLiveButton = (): ReactElement => {
  const isDesktop = useIsDesktop()
  const [dialogOpen, setDialogOpen] = useState(false)
  // Captured at click time, not render time — if the user navigates
  // (back/forward) between render and click, the QR must encode the
  // step they're actually on now.
  const [qrUrl, setQrUrl] = useState('')

  const handleClick = (): void => {
    if (isDesktop) {
      setQrUrl(withLiveParam(window.location.href))
      setDialogOpen(true)
    } else {
      setLiveMode()
    }
  }

  return (
    <>
      <Button variant="outline" size="xl" onClick={handleClick}>
        <Smartphone className="h-5 w-5" />
        Try it live
      </Button>
      {isDesktop && (
        <TryOnPhoneDialog open={dialogOpen} onOpenChange={setDialogOpen} url={qrUrl} />
      )}
    </>
  )
}
